/**
 * The routing record, rendered as the console would render it.
 *
 * Not an SVG. This is the one figure that should look like the product rather
 * than like a diagram of the product: a `RoutingDecision` card, the six scoring
 * components with their real weights, the four penalties, the arithmetic, and —
 * the part that matters — **the candidates that lost, each with the reason**.
 *
 * The single interaction is a real/simulated switch, and it is here because the
 * route simulator is a real feature (ACCEPTANCE_MATRIX M7): the same scoring
 * replayed against hypothetical inputs, taking no lease and starting no session.
 * Marking `srv-ist-1` offline re-ranks the candidates and changes the winner.
 *
 * There is no free-text input, deliberately. A live simulator on a static page
 * would be a lie about what is running; a two-state switch over two precomputed
 * outcomes is a demonstration of the feature, not a claim to be it.
 */
import { useState } from 'react'

import { Panel, Badge } from '../../chrome'
import { Stamp, MeterFill } from '../../kit'
import { useT, useLocale } from '../../i18n/context'
import { COMPONENTS, PENALTIES } from '../../data/routing'
import { CANDIDATES, DECISION, REJECTION_TR, REJECTION_EN } from '../../data/fixtures'
import './DecisionLedger.css'

/**
 * What the scores become when `srv-ist-1` goes offline.
 *
 * Precomputed rather than derived: the real recomputation reruns eligibility and
 * every component against fresh capacity snapshots, and a plausible-looking
 * arithmetic shim in a marketing page would be inventing numbers the engine did
 * not produce. Two honest snapshots beat one fake calculator.
 */
const OFFLINE_SCORES: Record<string, number | 'HOST_OFFLINE'> = {
  '01JQ8A1': 'HOST_OFFLINE',
  '01JQ8A3': 0.64,
  '01JQ8A4': 'HOST_OFFLINE',
  '01JQ8AB': 'HOST_OFFLINE',
  '01JQ8AC': 0.41,
}

export function DecisionLedger() {
  const t = useT()
  const locale = useLocale()
  const reasons = locale === 'en' ? REJECTION_EN : REJECTION_TR
  const [simulated, setSimulated] = useState(false)

  const survivors = CANDIDATES.filter((c) => c.reject === null)
  const rejected = CANDIDATES.filter((c) => c.reject !== null)

  const ranked = survivors
    .map((c) => {
      const sim = OFFLINE_SCORES[c.id]
      const offline = simulated && sim === 'HOST_OFFLINE'
      return {
        ...c,
        offline,
        effective: offline ? null : simulated && typeof sim === 'number' ? sim : (c.score ?? 0),
      }
    })
    .sort((a, b) => (b.effective ?? -1) - (a.effective ?? -1))

  const winner = ranked.find((c) => !c.offline)

  return (
    <Panel
      framed
      className="kl-ledger"
      title={
        <>
          {t('decision.fig.title')} <span className="k-mono kl-ledger__id">{DECISION.id}</span>
        </>
      }
      actions={
        <>
          <Badge tone={simulated ? 'info' : 'accent'}>
            {t(simulated ? 'ui.simulated' : 'ui.real')}
          </Badge>
          <button
            type="button"
            className="kl-ledger__toggle"
            aria-pressed={simulated}
            onClick={() => setSimulated((v) => !v)}
          >
            {t('decision.fig.toggle')}
          </button>
        </>
      }
    >
      <div className="kl-ledger__grid">
        <div className="kl-ledger__scores">
          <h3 className="kl-ledger__h">{t('ui.components')}</h3>
          <table className="kl-ledger__table">
            <thead>
              <tr>
                <th scope="col">{t('decision.fig.why')}</th>
                <th scope="col">{t('ui.weight')}</th>
              </tr>
            </thead>
            <tbody>
              {COMPONENTS.map((c) => (
                <tr key={c.key}>
                  <th scope="row" className="k-mono">
                    {c.key}
                  </th>
                  <td className="k-mono">{c.weight.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3 className="kl-ledger__h kl-ledger__h--pen">{t('ui.penalties')}</h3>
          <table className="kl-ledger__table">
            <tbody>
              {PENALTIES.map((p) => (
                <tr key={p.key}>
                  <th scope="row" className="k-mono">
                    {p.key}
                  </th>
                  <td className="k-mono kl-ledger__neg">−{p.weight.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="kl-ledger__total">
            <span>{t('ui.total')}</span>
            <Stamp className="k-mono kl-ledger__totalValue">
              {DECISION.componentTotal.toFixed(2)} − {DECISION.penaltyTotal.toFixed(2)} ={' '}
              {DECISION.total.toFixed(2)}
            </Stamp>
          </p>
        </div>

        <div className="kl-ledger__cands">
          <h3 className="kl-ledger__h">{t('ui.selected')}</h3>
          <ul className="kl-ledger__list">
            {ranked.map((c) => (
              <li key={c.id} className={`kl-ledger__cand ${c.id === winner?.id ? 'is-winner' : ''}`}>
                <span className="k-mono kl-ledger__acct">{c.account}</span>
                <span className="k-mono kl-ledger__host">{c.host}</span>
                {c.offline ? (
                  <Badge tone="err" mono>
                    HOST_OFFLINE
                  </Badge>
                ) : (
                  <>
                    <span className="k-mono kl-ledger__score">{c.effective?.toFixed(2)}</span>
                    <MeterFill
                      value={c.effective}
                      label={c.account}
                      unknownLabel={t('ui.unmeasured')}
                      tone={c.id === winner?.id ? 'accent' : 'info'}
                    />
                  </>
                )}
              </li>
            ))}
          </ul>

          <h3 className="kl-ledger__h kl-ledger__h--pen">{t('ui.rejected')}</h3>
          <ul className="kl-ledger__list kl-ledger__list--rej">
            {rejected.map((c) => (
              <li key={c.id} className="kl-ledger__rej">
                <span className="k-mono kl-ledger__acct">{c.account}</span>
                <span className="k-mono kl-ledger__code">{c.reject}</span>
                <span className="kl-ledger__reason">{reasons[c.reject!]}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Panel>
  )
}
